import {AuthForm, AuthHeading, AuthWrapper} from '@/components/ui/auth-atoms/AuthAtoms'
import i18n, {useTranslation} from '@/translations/i18n'
import {useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {Button} from '@/components/ui/button/Button'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {InputText} from '@/components/commons/input-text/InputText'
import {ButtonLink} from '@/components/ui/button-link/ButtonLink'
import {useState} from 'react'
import {routes} from '@/utilities/constants/routes'
import {errorHandler} from '@/utilities/helpers'
import {usePasswordRecovery} from '@/features/auth/services/usePasswordRecovery'
import {Helmet} from 'react-helmet'

export const ForgotPasswordValidationSchema = z.object({
    email: z.string().email({
        message: i18n.t('errors:email_error')
    })
})
export type ForgotPasswordValidationSchema = z.infer<typeof ForgotPasswordValidationSchema>

export const ForgotPassword = () => {
    const {t} = useTranslation()
    const [isRequestToRestorePasswordSent, setIsRequestToRestorePasswordSent] = useState(false)
    const form = useForm<ForgotPasswordValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(ForgotPasswordValidationSchema)
    })

    //login mutation
    const {mutate: passwordRecovery, isPending} = usePasswordRecovery({
        onError: error => errorHandler(error),
        onSuccess: () => setIsRequestToRestorePasswordSent(true)
    })

    const handleSubmit = form.handleSubmit(formValues => passwordRecovery(formValues))

    return (
        <AuthWrapper>
            <Helmet title={t('forgot_password:meta_title')} />
            <AuthForm fullWidth align="center" justify="center" gap={8} onSubmit={handleSubmit}>
                <AuthHeading
                    title={t('commons:forgot_password')}
                    subtitle={
                        isRequestToRestorePasswordSent
                            ? t('forgot_password:request_sent_paragraph')
                            : t('forgot_password:subtitle')
                    }
                />
                {isRequestToRestorePasswordSent ? (
                    <ButtonLink variant="primary" fullWidth to={routes.LOGIN.path}>
                        {t('forgot_password:back_button')}
                    </ButtonLink>
                ) : (
                    <>
                        <InputText
                            type="email"
                            label={t('commons:email')}
                            placeholder={t('commons:email_placeholder')}
                            errorMessage={form.formState.errors.email?.message}
                            {...form.register('email')}
                        />
                        <Flexbox direction="column" gap={4} align="center" fullWidth>
                            <Button type="submit" variant="primaryNeutral" fullWidth disabled={isPending}>
                                {t('commons:restore_password')}
                            </Button>
                            <ButtonLink disabled={isPending} variant="tertiary" shape="text" to={routes.LOGIN.path}>
                                {t('commons:login')}
                            </ButtonLink>
                        </Flexbox>
                    </>
                )}
            </AuthForm>
        </AuthWrapper>
    )
}

ForgotPassword.displayName = 'ForgotPassword'
